import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, TestTube2, Loader2, Save } from "lucide-react";
import { PageContainer, PageHeader, Pill } from "@/components/layout/PageShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPE_TONE = {
  Receipt: "blue",
  Kitchen: "amber",
  Label: "purple",
  Report: "default",
};

const EMPTY = {
  code: "",
  name: "",
  type: "Receipt",
  connection: "USB",
  device_name: "",
  assign_port: "",
  ip_address: "",
  location: "",
  counter_id: "",
  paper_width: 80,
  status: "Online",
  is_default: false,
};

export default function PrintersPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: printers = [], isLoading } = useQuery({
    queryKey: ["printers", branchId],
    queryFn: async () => {
      const { data } = await api.get("/printers", { params: { branch_id: branchId } });
      return data.printers;
    },
    enabled: Boolean(branchId),
  });

  const { data: counters = [] } = useQuery({
    queryKey: ["counters", branchId],
    queryFn: async () => {
      const { data } = await api.get("/counters", { params: { branch_id: branchId } });
      return data.counters;
    },
    enabled: Boolean(branchId),
  });

  const filtered = printers.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase()),
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        counter_id: form.counter_id ? Number(form.counter_id) : null,
        paper_width: Number(form.paper_width),
      };
      if (editingId) {
        return api.put(`/printers/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/printers", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["printers", branchId] });
      setDialogOpen(false);
      toast.success(editingId ? "Printer updated" : "Printer created");
    },
    onError: () => toast.error("Failed to save printer"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/printers/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["printers", branchId] });
      toast.success("Printer deleted");
    },
    onError: () => toast.error("Failed to delete printer"),
  });

  const testMutation = useMutation({
    mutationFn: (id) => api.post(`/printers/${id}/test`, {}, { params: { branch_id: branchId } }),
    onSuccess: (res) => toast.success(res.data.message),
    onError: () => toast.error("Test print failed"),
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY, code: `PRN-${String(printers.length + 1).padStart(2, "0")}` });
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      type: row.type,
      connection: row.connection,
      device_name: row.device_name || "",
      assign_port: row.assign_port || "",
      ip_address: row.ip_address || "",
      location: row.location || "",
      counter_id: row.counter_id ? String(row.counter_id) : "",
      paper_width: row.paper_width || 80,
      status: row.status,
      is_default: row.is_default,
    });
    setDialogOpen(true);
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <PageContainer>
      <PageHeader
        title="Printer Configuration"
        subtitle={`${printers.length} printers — ${user?.default_branch?.name || "branch"}`}
      />
      <DataToolbar searchValue={search} onSearch={setSearch} onAdd={openNew} addLabel="New Printer" testIdPrefix="prn" />

      {isLoading ? (
        <div className="h-32 grid place-items-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <DataTable
          testIdPrefix="prn"
          columns={[
            { key: "name", label: "Printer Name", bold: true },
            { key: "type", label: "Type", render: (v) => <Pill tone={TYPE_TONE[v] || "default"}>{v}</Pill> },
            { key: "connection", label: "Connection" },
            { key: "location", label: "Location" },
            { key: "status", label: "Status", render: (v) => <Pill tone={v === "Online" ? "green" : "red"}>{v}</Pill> },
          ]}
          rows={filtered}
          rowActions={(row) => [
            { title: "Test", icon: <TestTube2 size={14} />, onClick: () => testMutation.mutate(row.id) },
            { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
            {
              title: "Delete",
              icon: <Trash2 size={14} />,
              danger: true,
              onClick: () => {
                if (window.confirm(`Delete printer ${row.name}?`)) deleteMutation.mutate(row.id);
              },
            },
          ]}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Printer" : "New Printer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Code *">
              <Input value={form.code} onChange={(e) => setField("code", e.target.value)} className="h-9 font-mono" disabled={Boolean(editingId)} />
            </Field>
            <Field label="Name *">
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} className="h-9" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setField("type", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Receipt", "Kitchen", "Label", "Report"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Connection">
              <Select value={form.connection} onValueChange={(v) => setField("connection", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USB", "LAN", "Bluetooth", "Serial"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Device Name">
              <Input value={form.device_name} onChange={(e) => setField("device_name", e.target.value)} className="h-9 font-mono" placeholder="EPSON-TM-T88" />
            </Field>
            <Field label="Port">
              <Input value={form.assign_port} onChange={(e) => setField("assign_port", e.target.value)} className="h-9 font-mono" placeholder="USB001 / 9100" />
            </Field>
            <Field label="IP Address (LAN)">
              <Input value={form.ip_address} onChange={(e) => setField("ip_address", e.target.value)} className="h-9 font-mono" />
            </Field>
            <Field label="Location">
              <Input value={form.location} onChange={(e) => setField("location", e.target.value)} className="h-9" />
            </Field>
            <Field label="Counter">
              <Select value={form.counter_id || "none"} onValueChange={(v) => setField("counter_id", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {counters.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Paper Width">
              <Select value={String(form.paper_width)} onValueChange={(v) => setField("paper_width", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="58">58mm</SelectItem>
                  <SelectItem value="80">80mm</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Offline">Offline</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2 flex items-center justify-between py-1">
              <Label className="text-[12.5px]">Default for type</Label>
              <Switch checked={form.is_default} onCheckedChange={(v) => setField("is_default", v)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#16A34A] hover:bg-emerald-700" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="prn-save">
              {saveMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
